import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import ArticleCard from "@/components/ArticleCard";

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  useSeo({ title: `Search: ${q}`, description: "Search wolfdog news, legislation, guest articles, and resources across Wolfdog News." });
  const [results, setResults] = useState({ articles: [], guest_posts: [], radar: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!q) return;
    setLoading(true);
    api.get("/search", { params: { q } }).then((r) => setResults(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [q]);

  const total = results.articles.length + results.guest_posts.length + results.radar.length;

  return (
    <div className="max-w-7xl mx-auto px-4 py-14">
      <p className="overline text-[#8B6D1F]">Search Results</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0A192F] mt-1">"{q}"</h1>
      {loading ? (
        <p className="mt-8 text-[#4A5568]">Searching...</p>
      ) : (
        <>
          <p className="mt-2 text-[#4A5568]">{total} result{total !== 1 ? "s" : ""} found.</p>

          {results.articles.length > 0 && (
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-[#0A192F] mb-5">News &amp; Articles</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
                {results.articles.map((a) => <ArticleCard key={a.id} article={a} />)}
              </div>
            </section>
          )}

          {results.guest_posts.length > 0 && (
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-[#0A192F] mb-5">Guest Articles</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {results.guest_posts.map((g) => (
                  <Link key={g.id} to={`/articles/${g.id}`} className="bg-white border border-[#0A192F]/10 p-6 hover:border-[#D4AF37] transition-colors">
                    <span className="overline text-[#8B6D1F]">{g.category}</span>
                    <h3 className="font-display text-lg font-bold text-[#0A192F] mt-2">{g.title}</h3>
                    <p className="mt-2 text-sm text-[#4A5568] line-clamp-2">{g.excerpt}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {results.radar.length > 0 && (
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-[#0A192F] mb-5">From the News Radar</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {results.radar.map((r) => (
                  <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer" className="group bg-white border border-[#0A192F]/10 p-5 hover:border-[#D4AF37] transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="overline text-[#8B6D1F]">{r.source}</span>
                      <ExternalLink className="w-4 h-4 text-[#4A5568] group-hover:text-[#8B6D1F]" />
                    </div>
                    <h3 className="font-display text-lg font-bold text-[#0A192F]">{r.title}</h3>
                    <p className="mt-2 text-sm text-[#4A5568] line-clamp-2">{r.summary}</p>
                  </a>
                ))}
              </div>
            </section>
          )}

          {total === 0 && <p className="mt-10 text-[#4A5568]">No results. Try a different search term.</p>}
        </>
      )}
    </div>
  );
}
